import React, { useMemo, useState } from 'react';
import { format, subDays, startOfDay, isSameDay } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Calendar,
  IndianRupee,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  Users,
  Star,
  Activity,
} from 'lucide-react';
import AdminPillTabs from './ui/AdminPillTabs';
import { AdminBadge, AdminStatus } from './ui/AdminBadge';
import AdminButton from './ui/AdminButton';
import AdminBentoTile from './ui/AdminBentoTile';

const formatCurrency = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const QUICK_NAV_SIZES = ['1x1', '1x1', '2x1', '1x1', '1x1', '2x1', '1x1', '1x1', '1x1'];

const AdminOverview = ({
  analytics,
  bookings = [],
  navItems = [],
  onViewBookings,
  onNavigate,
  onRefresh,
}) => {
  const [bookingFilter, setBookingFilter] = useState('all');

  const chartData = useMemo(() => {
    const days = Array.from({ length: 14 }, (_, i) => {
      const date = startOfDay(subDays(new Date(), 13 - i));
      const count = bookings.filter((b) => {
        const raw = b.createdAt?.toDate?.() || b.createdAt || b.startDate;
        const d = raw ? new Date(raw) : null;
        return d && isSameDay(startOfDay(d), date);
      }).length;
      return {
        label: format(date, 'd MMM'),
        count,
        highlight: i === 13,
      };
    });
    return days;
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    const list = [...bookings].reverse();
    if (bookingFilter === 'all') return list.slice(0, 6);
    if (bookingFilter === 'pending') {
      return list.filter((b) => ['requested', 'pending'].includes(b.status)).slice(0, 6);
    }
    if (bookingFilter === 'active') {
      return list.filter((b) => b.status === 'in_progress').slice(0, 6);
    }
    if (bookingFilter === 'completed') {
      return list.filter((b) => b.status === 'completed').slice(0, 6);
    }
    return list.slice(0, 6);
  }, [bookings, bookingFilter]);

  const pillTabs = [
    { id: 'pending', label: 'Pending', count: analytics.pendingBookings },
    { id: 'active', label: 'In progress', count: analytics.activeBookings },
    { id: 'completed', label: 'Completed', count: analytics.completedBookings },
    { id: 'all', label: 'All', count: bookings.length },
  ];

  const regionData = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      const city = (b.caregiver?.city || b.caregiver?.location || 'Other').split(',')[0].trim();
      map[city] = (map[city] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));
  }, [bookings]);

  const maxRegion = Math.max(...regionData.map((r) => r.value), 1);

  const quickNavItems = navItems.filter((item) => item.id !== 'overview');

  return (
    <div className="space-y-6">
      {/* Hero bento row */}
      <div className="admin-bento-grid admin-bento-grid--dense">
        <AdminBentoTile
          size="2x1"
          variant="accent"
          icon={IndianRupee}
          title="Platform revenue"
          subtitle={`GMV ${formatCurrency(analytics.totalGMV)} · ${analytics.totalUsers} users`}
          value={formatCurrency(analytics.totalRevenue)}
        >
          {analytics.bookingSuccessRate > 0 && (
            <span className="inline-flex self-start items-center px-2.5 py-1 rounded-full bg-white/20 text-xs font-bold">
              +{analytics.bookingSuccessRate}% success rate
            </span>
          )}
        </AdminBentoTile>

        <AdminBentoTile
          size="1x1"
          variant="mint"
          icon={Calendar}
          title="Active bookings"
          value={analytics.activeBookings}
          onClick={onViewBookings}
          showArrow
          interactive
        />

        <AdminBentoTile
          size="1x1"
          variant="mint"
          icon={Users}
          title="Caregivers"
          value={analytics.totalCaregivers}
        />

        <AdminBentoTile
          size="3x2"
          icon={Activity}
          title="Booking activity"
          subtitle="Last 14 days"
        >
          <div className="admin-bento-tile__body flex-1 min-h-0">
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={onRefresh}
                className="p-2 rounded-xl hover:bg-[#f6f7f5] text-[#5c6670]"
                aria-label="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="h-40 sm:h-48 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barCategoryGap="18%">
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5c6670', fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis hide allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(12,15,20,0.04)' }}
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid #e8ece9',
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" radius={[6, 6, 6, 6]} maxBarSize={28}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.label}
                      fill={entry.highlight ? '#0c0f14' : '#c5d4cb'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
        </AdminBentoTile>

        <AdminBentoTile
          size="1x2"
          variant="mint"
          icon={TrendingUp}
          title="By region"
          subtitle="Top booking cities"
        >
          <div className="space-y-3 flex-1">
            {regionData.length === 0 ? (
              <p className="text-sm text-[#5c6670]">No booking regions yet</p>
            ) : (
              regionData.map((row) => (
                <div key={row.name}>
                  <div className="flex justify-between text-xs font-medium mb-1">
                    <span className="text-[#0c0f14]">{row.name}</span>
                    <span className="text-[#5c6670]">{row.value}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/60 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#0c0f14]"
                      style={{ width: `${(row.value / maxRegion) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </AdminBentoTile>

        <AdminBentoTile
          size="1x1"
          icon={Star}
          title="Avg rating"
          value={`${analytics.averageRating}/5`}
        />

        <AdminBentoTile
          size="1x1"
          icon={IndianRupee}
          title="Pending payouts"
          value={formatCurrency(analytics.pendingPayoutsTotal)}
        />

        <AdminBentoTile
          size="1x1"
          variant="dark"
          icon={Calendar}
          title="Pending"
          value={analytics.pendingBookings}
          onClick={() => onViewBookings?.()}
          showArrow
          interactive
        />

        <AdminBentoTile
          size="1x1"
          icon={TrendingUp}
          title="Completed"
          value={analytics.completedBookings}
        />
      </div>

      {/* Quick nav bento */}
      {quickNavItems.length > 0 && onNavigate && (
        <section>
          <p className="admin-bento-section-label mb-3">Quick access</p>
          <div className="admin-bento-grid admin-bento-grid--dense">
            {quickNavItems.map((item, index) => {
              const Icon = item.icon;
              const size = QUICK_NAV_SIZES[index % QUICK_NAV_SIZES.length];
              return (
                <AdminBentoTile
                  key={item.id}
                  size={size}
                  icon={Icon}
                  title={item.label}
                  onClick={() => onNavigate(item.id)}
                  showArrow
                  interactive
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Bookings bento panel */}
      <AdminBentoTile size="full" title="Recent bookings" subtitle="Latest platform activity">
        <div className="admin-bento-tile__body gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <AdminBadge count={bookings.length} />
            <AdminPillTabs tabs={pillTabs} active={bookingFilter} onChange={setBookingFilter} />
          </div>

          <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto_auto] gap-4 px-1 pb-1 text-xs font-bold uppercase tracking-wider text-[#5c6670]">
            <span>Booking</span>
            <span>Caregiver</span>
            <span>Pet</span>
            <span>Amount</span>
            <span>Status</span>
            <span />
          </div>

          <div className="space-y-2.5">
            {filteredBookings.length === 0 ? (
              <div className="text-center py-12 rounded-2xl border border-dashed border-[#e8ece9] bg-[#fafafa]">
                <Calendar className="w-10 h-10 text-[#c5d4cb] mx-auto mb-3" />
                <p className="text-sm font-medium text-[#5c6670]">No bookings in this view</p>
              </div>
            ) : (
              filteredBookings.map((booking) => (
                <div key={booking.id} className="admin-table-row">
                  <div>
                    <p className="text-sm font-bold text-[#0c0f14]">#{String(booking.id).slice(-6)}</p>
                    <p className="text-xs text-[#5c6670] mt-0.5">
                      {booking.startDate ? format(new Date(booking.startDate), 'd MMM yyyy') : '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img
                      src={booking.caregiver?.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=caregiver'}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover border border-[#e8ece9]"
                    />
                    <p className="text-sm font-medium text-[#0c0f14] truncate">
                      {booking.caregiver?.name || 'Caregiver'}
                    </p>
                  </div>
                  <p className="text-sm text-[#5c6670] truncate">{booking.pet?.name || '—'}</p>
                  <p className="text-sm font-semibold text-[#0c0f14]">
                    {formatCurrency(booking.escrowAmount || booking.totalAmount)}
                  </p>
                  <AdminStatus status={booking.status} />
                  <button
                    type="button"
                    onClick={onViewBookings}
                    className="text-sm font-semibold text-[#5c6670] hover:text-[#0c0f14] inline-flex items-center gap-1"
                  >
                    View
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {bookings.length > 0 && (
            <div className="flex justify-center pt-2">
              <AdminButton variant="ghost" onClick={onViewBookings}>
                View all bookings
              </AdminButton>
            </div>
          )}
        </div>
      </AdminBentoTile>
    </div>
  );
};

export default AdminOverview;
